export interface FormattedParagraph {
    spaceBefore: boolean;
    spaceAfter: boolean;
    parts: Array<FormattedParagraphComponent>;
}
export interface FormattedParagraphComponent {
    word: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
}

export type FormattedText = Array<FormattedParagraph>;
export type FormattedString = string;

export function toPlainString(val: FormattedString, params: object = {}): string {
    return toUnformattedString(toFormattedText(val, params));
}

export function toUnformattedString(text: FormattedText): string {
    if (!text) return '';
    let out = '';
    for (const paragraph of text) {
        if (paragraph.spaceBefore) out += "\n";
        for (const component of paragraph.parts) {
            out += component.word;
        }
        out += "\n";
    }
    return out;
}

export function toFormattedText(val: FormattedString, params: object = {}): FormattedText {
    return parseFormattedString(val, params);
}

function parseFormattedString(val: string, params: object = null): FormattedText {
    if (val) {
        const beforeAfterSpace = false;
        if (!params) params = {};
        const formattedText: FormattedText = [];
        const paragraphs = val.split(/\r?\n/);
        let spaceBefore = beforeAfterSpace;
        let lastLabel = null;
        for (let paragraph of paragraphs) {
            paragraph = paragraph.trim();
            if (!paragraph) {
                spaceBefore = true;
                continue;
            }
            if (paragraph.indexOf("* ") === 0) {
                paragraph = "●" + paragraph.substr(1);
            }
            const outparts = [];
            const components = paragraph.split("||");
            let format = false;
            components.forEach((c) => {
                const word = c;
                for (const part of parseFormattedComponent(word, false, false, false, format ? params : null)) {
                    outparts.push(part);
                }
                format = !format;
            });
            if (!spaceBefore && lastLabel) lastLabel.spaceAfter = false;
            lastLabel = { parts: outparts, spaceBefore: spaceBefore, spaceAfter: beforeAfterSpace };
            formattedText.push(lastLabel);
            spaceBefore = false;
        }
        return formattedText;
    } else {
        return null;
    }
}

function parseFormattedComponent(component: string, defaultBold: boolean, defaultItalic: boolean, defaultUnderline: boolean, params: object = null): Array<FormattedParagraphComponent> {
    const parts: Array<FormattedParagraphComponent> = [];
    let bold = 0;
    let italic = 0;
    let underline = 0;
    let segment = "";
    const isWhitespace = (c): boolean => {
        return c === ' ' || c === '\n' || c === '\r' || c === '\t';
    };
    const isBold = (c): boolean => {
        return c === '*';
    };
    const isItalic = (c): boolean => {
        return c === '/';
    };
    const isUnderline = (c): boolean => {
        return c === '_';
    };
    const atStart = (w, i): boolean => {
        const c = i >= 0 ? w.charAt(i) : null;
        if (!c) {
            return true;
        }
        if (isWhitespace(c)) {
            return true;
        }
        if (isUnderline(c) || isItalic(c) || isBold(c)) {
            return atStart(w, i - 1);
        }
        return false;
    };
    const atEnd = (w, i): boolean => {
        const c = i < w.length ? w.charAt(i) : null;
        if (!c) {
            return true;
        }
        if (isWhitespace(c)) {
            return true;
        }
        if (isUnderline(c) || isItalic(c) || isBold(c)) {
            return atEnd(w, i + 1);
        }
        return false;
    };
    const startFormatting = (w, i): boolean => {
        const c1 = component.charAt(i);
        const c2 = i + 1 < w.length ? w.charAt(i + 1) : null;
        if (atStart(w, i - 1) && !isWhitespace(c2)) {
            return isBold(c1) || isItalic(c1) || isUnderline(c1);
        }
        return false;
    };
    const endFormatting = (w, i): boolean => {
        const c0 = i - 1 >= 0 ? w.charAt(i - 1) : null;
        const c1 = component.charAt(i);
        if ((atEnd(w, i + 1) || (isBold(c1) && bold) || (isItalic(c1) && italic) || (isUnderline(c1) && underline)) && !isWhitespace(c0)) {
            return (bold && isBold(c1)) || (italic && isItalic(c1)) || (underline && isUnderline(c1));
        }
        return false;
    };
    for (let i = 0; i < component.length; i++) {
        const c = component.charAt(i);
        if (startFormatting(component, i)) {
            if (segment.length > 0) {
                parts.push({
                    word: params && params[segment] ? params[segment] : segment,
                    bold: bold > 0 ? true : defaultBold,
                    italic: italic > 0 ? true : defaultItalic,
                    underline: underline > 0 ? true : defaultUnderline
                });
            }
            if (isBold(c)) {
                bold++;
            } else if (isItalic(c)) {
                italic++;
            } else if (isUnderline(c)) {
                underline++;
            }
            segment = "";
        } else if (endFormatting(component, i)) {
            if (segment.length > 0) {
                parts.push({
                    word: params && params[segment] ? params[segment] : segment,
                    bold: bold > 0 ? true : defaultBold,
                    italic: italic > 0 ? true : defaultItalic,
                    underline: underline > 0 ? true : defaultUnderline
                });
            }
            if (isBold(c)) {
                bold--;
            } else if (isItalic(c)) {
                italic--;
            } else if (isUnderline(c)) {
                underline--;
            }
            segment = "";
        } else {
            segment += c;
        }
    }
    if (segment.length > 0) {
        parts.push({
            word: params && params[segment] ? params[segment] : segment,
            bold: bold > 0 ? true : defaultBold,
            italic: italic > 0 ? true : defaultItalic,
            underline: underline > 0 ? true : defaultUnderline
        });
    }
    return parts;
}
